import { useId, useState } from 'react';
import { healthcareErrorMessage } from '../healthcareErrors';
import { todayIso } from '../rateDisplay';

export interface EndDateFormProps {
  /** Accessible name of the group, for example "End assignment of Registered Nurse". */
  label: string;
  /** The date the row started; the end date must be after it. */
  startDate: string;
  /** What the date is called. The end is EXCLUSIVE: the first day the row no longer applies. */
  fieldLabel?: string;
  confirmLabel: string;
  onConfirm: (date: string) => Promise<void>;
  onCancel: () => void;
}

/** The small inline "end this row" control: a date, Confirm and Cancel. A refusal from the server is shown and the date stays. */
export function EndDateForm({ label, startDate, fieldLabel = 'Ends before', confirmLabel, onConfirm, onCancel }: EndDateFormProps) {
  const uid = useId();
  const [date, setDate] = useState(() => (todayIso() > startDate ? todayIso() : ''));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return setError('Enter an end date.');
    if (date < startDate) return setError('The end date cannot be before the date it started.');
    setError(null);
    setBusy(true);
    try {
      await onConfirm(date);
    } catch (err) {
      setError(healthcareErrorMessage(err, 'Could not end this row.'));
      setBusy(false);
    }
  }

  return (
    <div role="group" aria-label={label} className="hdc-end">
      <label htmlFor={`${uid}-date`}>{fieldLabel}</label>
      <input
        id={`${uid}-date`} type="date" value={date} aria-invalid={error ? true : undefined} aria-describedby={`${uid}-hint${error ? ` ${uid}-error` : ''}`}
        onChange={(e) => { setDate(e.target.value); setError(null); }}
      />
      <span id={`${uid}-hint`} className="jr-hint">The first day it no longer applies. Choose the start date itself ({startDate}) to void a row entered by mistake.</span>
      <div className="jr-actions">
        <button type="button" className="jr-btn jr-btn--primary jr-btn--small" disabled={busy} onClick={() => void confirm()}>{busy ? 'Ending…' : confirmLabel}</button>
        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      {error && <p id={`${uid}-error`} className="jr-error" role="alert">{error}</p>}
    </div>
  );
}
